import { useContext, useEffect, useState, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { TransactionalSubheader } from "iuvity-ui-components/dist/transactionalsubheader";
import { BoxFull } from "iuvity-ui-components/dist/boxfull";
import { Dropdown } from "iuvity-ui-components/dist/dropdown";
import { Button } from "iuvity-ui-components/dist/button";
import { CustomInput, Header } from "../../components";
import {
  CustomDialog,
  DialogProps,
} from "../../components/dialog/CustomDialog";
import { useConfigurations } from "../../hooks/useConfigurations";
import { securityKeyLabels } from "../../utils/index";
import { SecurityFormSchema } from "../../validations/schemas";
import {
  SECURITY_CODE_LENGTH_OPTIONS,
  SECURITY_CODE_TYPE_OPTIONS,
  REVALIDATE_FORM_PROPS,
} from "../../constants";
import { AppContext } from "../../context";
import { ErrorMessage } from "@hookform/error-message";

export const SecurityConfigurationPage = () => {
  const navigate = useNavigate();
  const {
    t,
    appState: { user },
    setAppState,
  } = useContext(AppContext);

  const { configurationAction, data } = useConfigurations("DATA");
  const [modalProps, setModalProps] = useState<DialogProps>({
    visible: false,
  } as DialogProps);

  const {
    control,
    reset,
    setValue,
    getValues,
    formState: { errors, isDirty, isValid },
  } = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
  });

  const keysOrder = useMemo(
    () => [
      "default.security.secondFactorMinimumLenght",
      "default.security.secondFactorIsNumeric",
      "default.security.secondFactorIsEnabledResendButton",
      "default.security.secondFactorResendTime",
      "default.security.secondFactorIsAutomaticTrigger",
    ],
    []
  );

  const onCancel = () => {
    setAppState((prev) => ({
      ...prev,
      selectedConfig: null,
    }));
    navigate("/", { replace: true });
    window.scrollTo(0, 0);
  };

  const onSave = async () => {
    const formValues = getValues();
    const securityInfo = Object.keys(formValues).map((key) => {
      const configInfo = data!.find(
        (config) => config.key.split(".").pop() === key
      );
      return {
        id: configInfo?.id,
        key: configInfo?.key,
        value: formValues[key],
        description: configInfo?.description,
        type: configInfo?.type,
        status: configInfo?.status,
      };
    });
    const actionStatus = await configurationAction("UPDATE", securityInfo);
    if (actionStatus === "SUCCESS") {
      reset(formValues);
      window.scrollTo(0, 0);
    }
  };

  const handleModalProps = (actionType: "cancel" | "save") => {
    switch (actionType) {
      case "save":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t("Security.saveModalTitle"),
          content: t("Security.saveModalContent"),
          onConfirm: onSave,
        }));
      case "cancel":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t("Security.cancelModalTitle"),
          content: t("Security.cancelModalContent"),
          onConfirm: onCancel,
        }));
      default:
        return;
    }
  };

  useEffect(() => {
    if (data) {
      const newValues = data
        .map((config) => {
          const valKey = config.key.split(".").pop() || "";
          return {
            [valKey]: config.value,
          };
        })
        .reduce((acc: any, current: any) => ({
          ...acc,
          ...current,
        }));
      reset(newValues);
    }
  }, [data, reset]);

  return (
    <>
      {/* <Header
        welcomeMessage={t("Dashboard.welcomeTitle")}
        userName={user?.name}
      /> */}
      <TransactionalSubheader
        subheaderTitle={
          <div>
            <i
              className="iu iu-icon-left pr-3"
              onClick={() => navigate("/")}
              aria-hidden="true"
              style={{
                fontSize: "var(--font-size-tablet-title)",
                cursor: "pointer",
              }}
            />
            {t("Security.securityTitle")}
          </div>
        }
      >
        <TransactionalSubheader.BreadCrumb
          id={"Autenticación y seguridad"}
          disabledDefaultHome={true}
          breadcrumbsHomeItem={{
            icon: "iu iu-icon-home",
            command: () => navigate("/"),
          }}
          breadcrumbsItems={[
            {
              label: t("Security.security2Breadcrumbs"),
              style: { fontWeight: "bold" },
            },
          ]}
        />
        <TransactionalSubheader.Title />
        <div className="t-subheader-content">
          <span>{t("Security.security1Description")}</span>
          <span>{t("Security.security2Description")}</span>
        </div>
      </TransactionalSubheader>

      {data && Object.keys(getValues()).length > 0 && (
        <BoxFull
          className="flex flex-column align-items-center justify-content-center mt-4 gap-4"
          variant="tertiary"
        >
          <div className="flex flex-column align-items-flex-start gap-4">
            <div className="inputlabel h4 pl-4">
              {t("Security.userAuthenticationTitleLabel")}
            </div>

            <div className="form-container center py-5 px-8">
              <div className="form center">
                {data
                  .filter(
                    ({ key }) =>
                      key === "default.security.enableRegistrationAtLogin"
                  )
                  .map(({ key, type }, idx, arr) => {
                    const newKey: string = key.split(".").pop() || "";
                    return (
                      <Controller
                        key={newKey}
                        name={newKey}
                        control={control}
                        render={({ field }) => {
                          if (type === "boolean") {
                            field.onChange = () => {
                              setValue(
                                field.name,
                                `${!(field.value === "true")}`,
                                REVALIDATE_FORM_PROPS
                              );
                            };
                          }
                          return (
                            <div
                              key={newKey}
                              className="flex flex-column"
                              style={{
                                width: "20rem",
                              }}
                            >
                              <CustomInput
                                id={`input-${newKey}`}
                                className={idx !== arr.length - 1 ? "mb-4" : ""}
                                label={t(securityKeyLabels(newKey).label) || ""}
                                placeholder={
                                  t(securityKeyLabels(newKey).placeholder) || ""
                                }
                                type={type}
                                autoComplete="off"
                                hasTooltip
                                tooltipText={t(
                                  "Security.enableRegistrationAtLoginTooltip"
                                )}
                                {...field}
                              />
                            </div>
                          );
                        }}
                      />
                    );
                  })}
              </div>
            </div>
          </div>

          <div className="flex flex-column align-items-flex-start gap-4">
            <div className="inputlabel h4 pl-4">
              {t("Security.transactionAuthenticationTitleLabel")}
            </div>

            <div className="form-container center py-5 px-8">
              <div className="form center">
                {data
                  .filter(
                    ({ key }) =>
                      key !== "default.security.enableRegistrationAtLogin"
                  )
                  .sort(function (a, b) {
                    return (
                      (keysOrder.indexOf(a.key) >>> 0) -
                      (keysOrder.indexOf(b.key) >>> 0)
                    );
                  })
                  .map(({ key, type }, idx, arr) => {
                    const newKey: string = key.split(".").pop() || "";
                    return (
                      <div key={`c-${newKey}`}>
                        <Controller
                          key={newKey}
                          name={newKey}
                          control={control}
                          rules={(SecurityFormSchema(t) as any)[key]}
                          render={({ field }) => {
                            const extraProps: any = {};
                            if (type === "boolean") {
                              field.onChange = () => {
                                setValue(
                                  field.name,
                                  `${!(field.value === "true")}`,
                                  REVALIDATE_FORM_PROPS
                                );
                              };
                            }
                            if (
                              key === "default.security.secondFactorResendTime"
                            ) {
                              extraProps.keyfilter = /^[0-9]*$/;
                              extraProps.maxLength = 2;
                            }
                            return [
                              "default.security.secondFactorMinimumLenght",
                              "default.security.secondFactorIsNumeric",
                            ].includes(key) ? (
                              // @ts-ignore
                              <Dropdown
                                className="dropdown mb-4"
                                label={t(securityKeyLabels(newKey).label) || ""}
                                placeholder=""
                                options={
                                  key ===
                                  "default.security.secondFactorMinimumLenght"
                                    ? SECURITY_CODE_LENGTH_OPTIONS
                                    : SECURITY_CODE_TYPE_OPTIONS(t)
                                }
                                filter={false}
                                showClear={false}
                                hasTooltipRef
                                tooltipRef={{
                                  content: t(securityKeyLabels(newKey).tooltip),
                                  position: "bottom",
                                  id: newKey,
                                }}
                                tooltipIdName={newKey}
                                {...field}
                              />
                            ) : (
                              <div
                                className="flex flex-column"
                                style={{
                                  width: "20rem",
                                }}
                              >
                                <CustomInput
                                  id={`input-${newKey}`}
                                  // className={idx !== arr.length - 1 ? "mb-4" : ""}
                                  className={
                                    // @ts-ignore
                                    errors[newKey]?.message
                                      ? "p-invalid"
                                      : idx !== arr.length - 1
                                      ? "mb-4"
                                      : ""
                                  }
                                  label={
                                    t(securityKeyLabels(newKey).label) || ""
                                  }
                                  placeholder={
                                    t(securityKeyLabels(newKey).placeholder) ||
                                    ""
                                  }
                                  type={type === "number" ? "text" : "boolean"}
                                  autoComplete="off"
                                  hasTooltip
                                  tooltipText={t(
                                    securityKeyLabels(newKey).tooltip
                                  )}
                                  {...field}
                                  {...extraProps}
                                />
                              </div>
                            );
                          }}
                        />
                        {key === "default.security.secondFactorResendTime" && (
                          <ErrorMessage
                            errors={errors}
                            name={newKey}
                            //@ts-ignore
                            render={({ message }) => (
                              <small
                                className="p-error block pl-5 ml-2"
                                style={{
                                  marginTop: "0.5rem",
                                  marginBottom: "1.5rem",
                                  alignSelf: "flex-start",
                                }}
                              >
                                {message}
                              </small>
                            )}
                          />
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>

          <div className="form-btngroup-footer my-5 p-0 gap-6">
            <Button
              variant="p-button-link"
              label={t("Security.cancelButton")}
              onClick={isDirty ? () => handleModalProps("cancel") : onCancel}
            />
            <Button
              variant="p-button"
              label={t("Security.saveButton")}
              onClick={() => handleModalProps("save")}
              disabled={!(isValid && isDirty)}
            />
          </div>
        </BoxFull>
      )}

      <CustomDialog
        title={modalProps.title}
        content={modalProps.content}
        visible={modalProps.visible}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
      />
    </>
  );
};
